<template>
  <div class="border-2 border-slate-200 bg-white shadow-sm" :class="hasClassification ? 'border-emerald-300 bg-emerald-50/30' : 'border-amber-300 bg-amber-50/30'">
    <div
      class="border-b px-4 py-3"
      :class="hasClassification ? 'border-emerald-200 bg-emerald-100/50' : 'border-amber-200 bg-amber-100/50'">
      <h2 class="text-base font-semibold" :class="hasClassification ? 'text-emerald-900' : 'text-amber-900'">
        {{ hasClassification ? 'Classificação do alarme' : 'Classificar alarme' }}
      </h2>
      <p class="mt-0.5 text-sm" :class="hasClassification ? 'text-emerald-800' : 'text-amber-800'">
        {{ hasClassification ? 'Este alarme já foi classificado.' : 'Selecione uma classificação para este alarme.' }}
      </p>
    </div>

    <!-- Já classificado: ênfase em quem (nome) e quando; botão lápis para editar -->
    <div v-if="hasClassification" class="p-4">
      <div class="flex flex-wrap items-center gap-4 border border-emerald-200 bg-white p-4">
        <div class="flex flex-1 flex-wrap items-center gap-4">
          <div>
            <p class="text-xs font-semibold uppercase tracking-wide text-slate-500">Classificação</p>
            <p class="mt-1 text-lg font-semibold text-slate-900">{{ alarm.classification_type?.name ?? '—' }}</p>
          </div>
          <div class="h-8 w-px bg-slate-200" />
          <div>
            <p class="text-xs font-semibold uppercase tracking-wide text-slate-500">Natureza</p>
            <span
              v-if="alarm.classification_type?.nature"
              :class="getClassificationTypeNatureBadgeClass(alarm.classification_type.nature)"
              class="mt-1 inline-flex items-center px-2 py-0.5 text-xs font-medium">
              {{ getClassificationTypeNatureLabel(alarm.classification_type.nature) }}
            </span>
            <p v-else class="mt-1 font-medium text-slate-900">—</p>
          </div>
          <div class="h-8 w-px bg-slate-200" />
          <div>
            <p class="text-xs font-semibold uppercase tracking-wide text-slate-500">Classificado por</p>
            <p class="mt-1 font-medium text-slate-900">{{ classifiedByLabel }}</p>
          </div>
          <div class="h-8 w-px bg-slate-200" />
          <div>
            <p class="text-xs font-semibold uppercase tracking-wide text-slate-500">Classificado em</p>
            <p class="mt-1 font-medium tabular-nums text-slate-900">{{ classifiedAtLabel }}</p>
          </div>
        </div>
        <button
          v-if="canEditClassification && !isEditingClassification"
          type="button"
          class="ml-auto flex shrink-0 items-center justify-center rounded-full p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
          title="Alterar classificação"
          @click="isEditingClassification = true">
          <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
        </button>
      </div>
      <!-- Admin/manager: formulário de alteração (abre ao clicar no lápis) -->
      <div v-if="canEditClassification && isEditingClassification" class="mt-4 border-t border-emerald-200 pt-4">
        <div class="flex flex-wrap items-end gap-4">
          <div class="min-w-[200px] flex-1">
            <label class="mb-1 block text-xs font-medium text-slate-600">Nova classificação</label>
            <Dropdown
              name="classification_edit"
              label-attribute="name"
              :options="classifications"
              placeholder="Selecione..."
              :already-selected-option="editClassification ?? undefined"
              @option-selected="onEditClassificationSelected"
            >
              <template #option="{ option }">
                <div class="flex items-center justify-between gap-3">
                  <span class="truncate">{{ option.name }}</span>
                  <span
                    v-if="option.nature"
                    :class="getClassificationTypeNatureBadgeClass(option.nature)"
                    class="flex shrink-0 items-center px-2 py-0.5 text-xs font-medium">
                    {{ getClassificationTypeNatureLabel(option.nature) }}
                  </span>
                </div>
              </template>
            </Dropdown>
          </div>
          <label v-if="isAlarmOpen" class="flex cursor-pointer select-none items-center gap-2 pb-2">
            <input
              v-model="shouldEndAlarm"
              type="checkbox"
              class="h-4 w-4 border-stroke text-primary focus:ring-primary"
            />
            <span class="text-sm font-medium text-slate-700">Encerrar alarme</span>
          </label>
          <button
            type="button"
            :disabled="!editClassification || isSubmitting"
            class="inline-flex items-center justify-center border border-slate-600 bg-slate-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:border-slate-700 hover:bg-slate-700 disabled:border-slate-300 disabled:bg-slate-300 disabled:cursor-not-allowed"
            @click="submitEdit">
            <span v-if="isSubmitting" class="mr-2 inline-block h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
            {{ isSubmitting ? 'Salvando...' : 'Salvar' }}
          </button>
          <button
            type="button"
            class="inline-flex items-center justify-center border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50"
            :disabled="isSubmitting"
            @click="isEditingClassification = false">
            Cancelar
          </button>
        </div>
        <p v-if="errorMessage" class="mt-2 text-sm text-red-600">{{ errorMessage }}</p>
        <p v-if="successMessage" class="mt-2 text-sm text-emerald-700">{{ successMessage }}</p>
      </div>
    </div>

    <!-- Formulário de classificação manual (alarme ainda não classificado) -->
    <div v-else class="p-4">
      <p v-if="classifications.length === 0" class="text-sm text-slate-500">
        Nenhum tipo de classificação disponível. Configure os tipos na área de Alarmes.
      </p>
      <template v-else>
        <div class="flex flex-wrap items-end gap-4">
          <div class="min-w-[200px] flex-1">
            <label class="mb-1 block text-sm font-medium text-slate-700">Classificação</label>
            <Dropdown
              name="classification"
              label-attribute="name"
              :options="classifications"
              placeholder="Selecione..."
              :already-selected-option="selectedClassification ?? undefined"
              @option-selected="onClassificationSelected"
            >
              <template #option="{ option }">
                <div class="flex items-center justify-between gap-3">
                  <span class="truncate">{{ option.name }}</span>
                  <span
                    v-if="option.nature"
                    :class="getClassificationTypeNatureBadgeClass(option.nature)"
                    class="flex shrink-0 items-center px-2 py-0.5 text-xs font-medium">
                    {{ getClassificationTypeNatureLabel(option.nature) }}
                  </span>
                </div>
              </template>
            </Dropdown>
          </div>
          <label v-if="isAlarmOpen" class="flex cursor-pointer select-none items-center gap-2 pb-2">
            <input
              v-model="shouldEndAlarm"
              type="checkbox"
              class="h-4 w-4 border-stroke text-primary focus:ring-primary"
            />
            <span class="text-sm font-medium text-slate-700">Encerrar alarme</span>
          </label>
          <button
            type="button"
            :disabled="!selectedClassification || isSubmitting"
            class="inline-flex items-center justify-center border border-slate-600 bg-slate-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:border-slate-700 hover:bg-slate-700 disabled:border-slate-300 disabled:bg-slate-300 disabled:cursor-not-allowed"
            @click="submit">
            <svg v-if="!isSubmitting" class="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span v-else class="mr-2 inline-block h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
            {{ isSubmitting ? 'Salvando...' : 'Salvar' }}
          </button>
        </div>
        <p v-if="errorMessage" class="mt-2 text-sm text-red-600">{{ errorMessage }}</p>
        <p v-if="successMessage" class="mt-2 text-sm text-emerald-700">{{ successMessage }}</p>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import Dropdown from '@/components/Globals/Inputs/Dropdown.vue'
import {
  getClassificationTypeNatureBadgeClass,
  getClassificationTypeNatureLabel,
} from '@/interfaces/enums'
import { computed, ref, watch } from 'vue'

interface IClassificationTypeOption {
  id: number
  name: string
  nature?: string | null
}

const props = withDefaults(
  defineProps<{
    alarm: {
      classification_type?: IClassificationTypeOption | null
      classified_by?: string | null
      classified_at?: string | null
      updated_at?: string | null
      created_at?: string | null
      end_date?: string | null
    }
    classifications: IClassificationTypeOption[]
    /** Se true (admin/manager), permite editar a classificação mesmo já atribuída */
    canEditClassification?: boolean
  }>(),
  { canEditClassification: false },
)

const emit = defineEmits<{
  (e: 'submit', payload: { classificationId: number; endAlarm: boolean }): void
  (e: 'classified', classification: IClassificationTypeOption): void
}>()

const selectedClassification = ref<IClassificationTypeOption | null>(null)
const shouldEndAlarm = ref(false)
const editClassification = ref<IClassificationTypeOption | null>(null)
const isSubmitting = ref(false)
const errorMessage = ref('')
const successMessage = ref('')
const isEditingClassification = ref(false)

const hasClassification = computed(() => !!props.alarm?.classification_type)
const isAlarmOpen = computed(() => !props.alarm?.end_date)

watch(
  () => props.alarm?.classification_type,
  (ct) => {
    if (ct) editClassification.value = ct
  },
  { immediate: true },
)

const classifiedByLabel = computed(() => {
  const who = props.alarm?.classified_by
  return who && who.trim() ? who : '—'
})

const classifiedAtLabel = computed(() => {
  const at = props.alarm?.classified_at ?? props.alarm?.updated_at ?? props.alarm?.created_at
  if (!at) return '—'
  return new Date(at).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
})

function onClassificationSelected(option: IClassificationTypeOption) {
  selectedClassification.value = option
  errorMessage.value = ''
}

function onEditClassificationSelected(option: IClassificationTypeOption) {
  editClassification.value = option
  errorMessage.value = ''
}

function submit() {
  if (!selectedClassification.value) {
    errorMessage.value = 'Selecione uma classificação.'
    return
  }
  errorMessage.value = ''
  successMessage.value = ''
  isSubmitting.value = true
  emit('submit', {
    classificationId: selectedClassification.value.id,
    endAlarm: shouldEndAlarm.value,
  })
}

function submitEdit() {
  if (!editClassification.value) {
    errorMessage.value = 'Selecione uma classificação.'
    return
  }
  errorMessage.value = ''
  successMessage.value = ''
  isSubmitting.value = true
  emit('submit', {
    classificationId: editClassification.value.id,
    endAlarm: shouldEndAlarm.value,
  })
}

function setResult(success: boolean, message: string) {
  isSubmitting.value = false
  if (success) {
    shouldEndAlarm.value = false
    successMessage.value = message
    isEditingClassification.value = false
    const updated = selectedClassification.value || editClassification.value
    if (updated) {
      emit('classified', updated)
      editClassification.value = updated
    }
  } else {
    errorMessage.value = message
  }
}

defineExpose({ setResult })
</script>
